// @ts-strict-ignore
import { deleteProperty } from 'dot-prop';
import { extractThresholdIds } from '../../../service/thresholds/thresholds';
import type { Action, SchemaInstance } from '../types';
import { deleteThresholdFromBooleanRule } from './rules/rules';
import { removeDataRouteFromExpression } from './expression';
import { ActionType } from '../constants';
import { isActionOfType } from 'flow/service/actions';

export function modifyRelevantDataFromActionRemoval({
  originalTargetValue,
  workflowData,
}: {
  originalTargetValue: Action;
  workflowData: Partial<SchemaInstance>;
}) {
  const thresholdIdsForActionThatGotRemoved = extractThresholdIds(originalTargetValue.rules);
  if (thresholdIdsForActionThatGotRemoved.length) {
    thresholdIdsForActionThatGotRemoved.forEach(threshold => {
      deleteProperty(
        workflowData.thresholds,
        threshold,
      );
    });
  }

  const thresholdsThatUseRemovedActionAsLeftRightAction = [];
  Object.values(workflowData.thresholds).forEach(threshold => {
    if ([
      threshold.left.actionId,
      threshold.right.actionId,
    ].includes(originalTargetValue.publicId)) {
      thresholdsThatUseRemovedActionAsLeftRightAction.push({
        thresholdId: threshold.publicId,
        actionId: threshold.actionId, // this is the action where this threshold is referenced in its boolean rule
      });
    }
  });

  thresholdsThatUseRemovedActionAsLeftRightAction.forEach(removableThreshold => {
    deleteProperty(
      workflowData.thresholds,
      removableThreshold.thresholdId,
    );
    const targetAction = workflowData.actions[removableThreshold.actionId];
    if (!targetAction) return;
    const updatedRule = deleteThresholdFromBooleanRule({
      thresholdId: removableThreshold.thresholdId,
      originalRule: targetAction.rules || {},
    });

    const isUpdatedRuleEmpty = Object.keys(updatedRule).length === 0
      || Object.values(updatedRule).length === 0
      || Object.values(updatedRule)?.[0].length === 0;

    const canExistWithoutRules = isActionOfType(
      targetAction,
      [ActionType.SERVICE, ActionType.MATRIX_MODEL],
    );

    if (isUpdatedRuleEmpty && !canExistWithoutRules) {
      // remove action from graph
      deleteProperty(
        workflowData.actions,
        removableThreshold.actionId,
      );

      modifyRelevantDataFromActionRemoval({
        originalTargetValue: targetAction,
        workflowData,
      });
    } else {
      workflowData.actions[removableThreshold.actionId].rules = updatedRule;
    }
  });

  const dataRoutesThatReferenceRemovedAction = [];
  Object.values(workflowData.dataRoutes).forEach(dataRoute => {
    if ([dataRoute.sourceId, dataRoute.targetId].includes(originalTargetValue.publicId)) {
      dataRoutesThatReferenceRemovedAction.push({
        object: dataRoute,
        type: dataRoute.sourceId === originalTargetValue.publicId
          ? 'source'
          : 'target',
      });
    }
  });

  if (dataRoutesThatReferenceRemovedAction.length) {
    dataRoutesThatReferenceRemovedAction.forEach(removableDataRoute => {
      const { targetId, publicId } = removableDataRoute.object;
      if (removableDataRoute.type === 'source') {
        // if type is source: the data route is referencing the removed action in the targetId key. this means this data route is referenced in the source action.
        const targetAction = workflowData.actions[targetId];

        if (targetAction) {
          if (targetAction.type === ActionType.MATRIX_MODEL) {
            const { matrixModel } = workflowData.actions[targetId];
            if (matrixModel.dataRouteX === publicId) {
              workflowData.actions[targetId].matrixModel.dataRouteX = '';
            } else if (matrixModel.dataRouteY === publicId) {
              workflowData.actions[targetId].matrixModel.dataRouteY = '';
            }
          }

          if (targetAction.type === ActionType.OUTPUT) {
            const updatedExpression = removeDataRouteFromExpression({
              dataRouteId: publicId,
              originalExpression: targetAction.output.expression,
            });

            workflowData.actions[targetId].output.expression = updatedExpression;
          }
        }
      }

      deleteProperty(
        workflowData.dataRoutes,
        publicId,
      );
    });
  }

  if (workflowData.validations?.actions) {
    workflowData.validations.actions = workflowData.validations.actions.filter(
      action => action.publicId !== originalTargetValue.publicId);
  }
}
